import { useRuntimeConfig } from '#imports'
import { onMounted } from 'vue'

import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useIsWebviewUserAgent } from '@backmarket/nuxt-module-navigation/useIsWebviewUserAgent'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

import { generateAuthState, getStoredAuthState } from '../utils/authState'
import { scopedStorage } from '../utils/storage'

import { useAuthOrigin } from './useAuthOrigin'
import { usePkce, useStoredPkceVerifier } from './usePkce'
import { useQueryParam } from './useQueryParam'
import { NEXT_KEY, useRedirect } from './useRedirect'
import { useRevokeToken } from './useRevokeToken'
import { useSession } from './useSession'
import { useTokens } from './useTokens'
import { useUserStore } from './useUserStore'

/**
 * This represents the case where the random state passed to and eventually
 * received from Hydra does not match the one stored inside our shared storage.
 */
class InvalidStateError extends Error {}

/**
 * Handles the whole login flow, from A to Z.
 *
 * This composable is the single entry point to easily manage whole authentication flows based on the Identity stack.
 *
 * It must be used on a loading page. It will handle both the start and end of the auth flow.
 * For the start of the flow, it will redirect users to the auth stack where they can create a new account or log in with an existing one.
 * For the end of the flow, it will serve as a callback URL to exchange the OAuth authorization code for tokens, create a session and store the user.
 *
 * @param {Object} options - The options object
 * @param {string} options.redirectionPathname - The callback URL in your app where the auth stack will redirect with the OAuth authorization code. This URL (usually the same as the one where the login process is started) must be a loading page that uses this composable.
 * @param {Function} options.onError - Callback function to handle any errors during the login process
 * @param {string|null} [options.bmJourney=null] - Optional journey identifier
 *
 * @example
 * useLogin({
 *   redirectionPathname: `/${locale}/auth/login`,
 *   onError(error) {
 *     if (error instanceof Error) {
 *       logger.error('Something failed while logging in', {
 *         error,
 *         owners: ['bot-squad-identity'],
 *       })
 *     }
 *   }
 * })
 *
 * The `useLogin` composable also manages the redirection after a successful
 * login. By default, it will redirect to the `authenticated` level of the
 * `defaultRedirectLocations` value
 * passed when initialising the OAuth module. But, it can be overriden by
 * setting a `next` query parameter into the URL.
 *
 * @example https://www.backmarket.com/es-us/auth/login?next=/dashboard
 *
 * External links are also supported in the `next` query parameter. However,
 * they must be first allowed when instantiating the module on the consumer
 * application. This is achieved via the `allowedExternalDomains` option.
 *
 * @example https://www.backmarket.com/es-us/auth/login?next=https://www.partner.com
 */
export function useLogin({
  redirectionPathname,
  onError,
  bmJourney = null,
  bmAutoStartProvider = null,
}: {
  redirectionPathname: string
  onError: (error: unknown) => void
  bmJourney?: string | null
  bmAutoStartProvider?: string | null
}) {
  onMounted(async () => {
    const { redirectAfterAuth } = useRedirect()
    const { trackScreenView } = useTracking()
    const { getTokens } = useTokens()
    const { getSessionCookie } = useSession()
    const { revokeToken } = useRevokeToken()

    try {
      const { location } = window
      const authOrigin = useAuthOrigin()
      const runtimeConfig = useRuntimeConfig()
      const { clientId, scope: defaultScope } = runtimeConfig.public.oauth
      const scope = useQueryParam('scope') ?? defaultScope
      const AUTH_ENDPOINT = '/oauth2/auth'

      const redirectUrl = new URL(redirectionPathname, location.origin)
      const userStore = useUserStore()

      // If there is no `next` parameter, there is no need to store anything.
      // We simply redirect to the fallback page when coming back to this route.
      const code = useQueryParam('code')
      const isLoginStart = code === null
      const next = useQueryParam('next')
      if (next !== null) {
        scopedStorage.setItem(NEXT_KEY, JSON.stringify({ next }))
      } else if (isLoginStart) {
        // Unset the key 'next' if the query param was not set during login start.
        // This avoids leakage from a previous useLogin start.
        // If no query param, we want to fallback on the default redirection URL.
        scopedStorage.removeItem(NEXT_KEY)
      }

      // The `code` query parameter is missing when we visit the login page for
      // the first time in the session (vs. going back from the identity stack).
      if (isLoginStart) {
        trackScreenView({
          screenType: 'auth_flow_start',
        })

        const pkce = usePkce()
        const state = generateAuthState()
        const locale = useI18nLocale()

        const isWebviewUserAgent = useIsWebviewUserAgent()

        const authUrl = new URL(AUTH_ENDPOINT, authOrigin)

        const queryParams = {
          bm_platform: isWebviewUserAgent ? 'mobile' : 'web',
          code_challenge: pkce.challenge,
          code_challenge_method: 'S256',
          redirect_uri: redirectUrl.href,
          response_type: 'code',
          scope,
          state,
          client_id: clientId,
          locale,
          bm_journey: useQueryParam('bm_journey') ?? bmJourney,
          bm_auto_start_provider:
            useQueryParam('bm_auto_start_provider') ?? bmAutoStartProvider,
          info: useQueryParam('info'),
          email: useQueryParam('email'),
          first_name: useQueryParam('first_name'),
          last_name: useQueryParam('last_name'),
          webview:
            useQueryParam('webview') ?? (isWebviewUserAgent ? 'true' : null),
        }

        Object.entries(queryParams).forEach(([key, value]) => {
          if (value) {
            authUrl.searchParams.append(key, value)
          }
        })

        // using `location.replace` to prevent current page from being added to the history stack
        // this would cause a back navigation to a transient loading page
        location.replace(authUrl)

        return
      }

      trackScreenView({
        screenType: 'auth_flow_end',
      })

      const state = useQueryParam('state')
      const storedState = getStoredAuthState()

      if (state !== storedState) {
        throw new InvalidStateError('State and stored state do not match')
      }

      const verifier = useStoredPkceVerifier()

      const tokens = await getTokens({ code, clientId, redirectUrl, verifier })

      await getSessionCookie(tokens.accessToken)

      const safeFetchUser = async () => {
        try {
          // This is a workaround to reduce the number of cases where a user ends up with an invalid csrftoken.
          // Cf. https://backmarket.atlassian.net/browse/FRONT-1259 for more information.
          // Note that it is still theoretically possible to have a `fetchUser` request made before the `useSession` and end after this new `fetchUser`.
          // TODO: [FRONT-1259] Replace this workaround with a proper fix.
          await userStore.fetchUser()
        } catch (error) {
          useLogger().error('[AUTH] Failed to fetch bm/user/auth', {
            error: error as Error,
            owners: ['bot-squad-identity'],
          })
        }
      }

      await Promise.all([
        revokeToken({
          refreshToken: tokens.refreshToken,
          clientId,
        }),
        safeFetchUser(),
      ])

      await redirectAfterAuth()
    } catch (error) {
      onError(error)

      // If anything goes wrong, we want to redirect the user to the next page
      // anyway. It will likely retrigger a chain of redirects and they will
      // end up on the login page once again, but with fresh data this time.
      await redirectAfterAuth()
    }
  })
}
